import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Typography, Row, Col, Space, Segmented, DatePicker, Button, Dropdown, Divider, Badge, Card, Tooltip } from "antd";
import {
    LockOutlined, PlusOutlined, DownOutlined, FileTextOutlined, DownloadOutlined,
    MinusOutlined, PieChartOutlined, BookOutlined, SwapOutlined, BankOutlined,
    AuditOutlined, DollarOutlined, ClusterOutlined, SolutionOutlined, FileExcelOutlined,
    DeleteOutlined, HistoryOutlined, UnlockOutlined, RestOutlined, CloudDownloadOutlined,
    HolderOutlined, FullscreenOutlined, FullscreenExitOutlined, WindowsOutlined,
    InfoCircleOutlined, SearchOutlined, QuestionCircleOutlined, CloseOutlined,
    AppstoreOutlined, LayoutOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { toast } from "react-toastify";

import { Method_GET_CONTEXTO, Method_POST_CONTEXTO } from '../../../../service/api';
import { serviceDownloadBalancete } from '../../../../service/downloadService';

import { processarBalanceteContabil } from './utils';
import BalanceteHeader from './components/BalanceteHeader';
import BalanceteTable from './components/BalanceteTable';
import BalanceteModals from './components/BalanceteModals';
import BalanceteBanner from './components/BalanceteBanner';

import './style.css';

dayjs.extend(isSameOrBefore);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// IDs de usuários com acesso total (incluindo ações de alteração)
const IDS_AUTORIZADOS = [
    "anderson",
    "bruno",
    "guilherme ramos",
    "jheneffer.aleixo",
    "patriciaa",
    "paulinealves",
    "ricardocosta",
    "thais juliao",
    "thaiscastilho"
];

export default function Balancete({ onDataChange }) {
    // --- Estados ---
    const [filterType, setFilterType] = useState("mensal");
    const [mesCompetencia, setMesCompetencia] = useState(dayjs());
    const [periodo, setPeriodo] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);

    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [periodoFechado, setPeriodoFechado] = useState(false);
    const [mesesBloqueados, setMesesBloqueados] = useState([]);
    const [defisPendencia, setDefisPendencia] = useState("");

    // Estado consolidado de Modals
    const [modals, setModals] = useState({
        dre: false,
        livroRazao: false,
        editLancamento: false,
        lancamentosBancarios: false,
        balanco: false,
        pagamentos: false,
        contas: false,
        informe: false,
        excel: false,
        delete: false,
        regerar: false,
        bloqueio: false,
        deletarBloqueio: false,
        zeramento: false,
        exportExcel: false,
        faturamento: false,
        folha: false,
        patrimonio: false,
        dashboard: false,
        checklist: false
    });

    const [faturamentoData, setFaturamentoData] = useState(null);

    const [contaSelecionada, setContaSelecionada] = useState(null);
    const [razaoAbertoPor, setRazaoAbertoPor] = useState(null);
    const [editingLancamentoId, setEditingLancamentoId] = useState(null);
    const [minimized, setMinimized] = useState([]); // Array de chaves dos modais minimizados
    const [showShortcuts, setShowShortcuts] = useState(true);
    const [zIndices, setZIndices] = useState({});
    const [maxZIndex, setMaxZIndex] = useState(1000);
    const [isContador, setIsContador] = useState(true);

    // Estado para arrastar a barra de tarefas e atalhos
    const [taskbarPos, setTaskbarPos] = useState({ x: window.innerWidth / 2, y: window.innerHeight - 80 });
    const [shortcutPos, setShortcutPos] = useState({ x: window.innerWidth - 234, y: 70 });
    const [isTaskbarCompact, setIsTaskbarCompact] = useState(false);
    const draggingTarget = useRef(null); // 'taskbar' ou 'shortcut'
    const offset = useRef({ x: 0, y: 0 });

    const clienteId = sessionStorage.getItem("COMPANY") ? JSON.parse(sessionStorage.getItem("COMPANY"))?.id : null;
    const filterRef = useRef(null);
    const zCounterRef = useRef(10000);

    const focusModal = useCallback((key) => {
        const currentGlobal = window.__DRAGGABLE_TOP_Z || 10000;
        if (currentGlobal > zCounterRef.current) {
            zCounterRef.current = currentGlobal;
        }

        zCounterRef.current += 1;
        window.__DRAGGABLE_TOP_Z = zCounterRef.current;

        setZIndices(prev => ({
            ...prev,
            [key]: zCounterRef.current,
        }));
    }, []);

    const toggleModal = useCallback((key, visible, extraState = {}) => {
        console.log(`toggleModal: [${key}] -> ${visible}`, extraState);
        if (!visible) console.trace(`Closing modal: ${key}`);
        setModals(prev => ({ ...prev, [key]: visible }));

        if (visible) {
            setMinimized(prev => prev.filter(m => m !== key));
            focusModal(key);
        }

        if (extraState.contaId !== undefined) setContaSelecionada(extraState.contaId);
        if (extraState.context !== undefined) setRazaoAbertoPor(extraState.context);
        if (extraState.reset) setEditingLancamentoId(null);
    }, [focusModal]);

    const minimizeModal = useCallback((key) => {
        setModals(prev => ({ ...prev, [key]: false }));
        setMinimized(prev => {
            if (!prev.includes(key)) return [...prev, key];
            return prev;
        });
    }, []);

    const modalNames = {
        dre: "DRE",
        livroRazao: "Livro Razão",
        editLancamento: "Lançamento",
        lancamentosBancarios: "Conciliação",
        balanco: "Balanço",
        pagamentos: "Pagamentos",
        contas: "Plano de Contas",
        informe: "Informe",
        excel: "Importar Excel",
        delete: "Excluir",
        regerar: "Regerar",
        bloqueio: "Bloquear",
        deletarBloqueio: "Desbloquear",
        zeramento: "Zeramento",
        exportExcel: "Exportar",
        faturamento: "Faturamento",
        folha: "Informações da Folha",
        patrimonio: "Patrimônio",
        dashboard: "Dashboard",
        checklist: "Checklist"
    };

    // --- Efeitos ---

    useEffect(() => {
        // BLOQUEIO DESATIVADO PARA TESTES
        setIsContador(true);
    }, []);

    // Sincroniza período com mês de competência se o filtro for mensal
    useEffect(() => {
        if (filterType === "mensal" && mesCompetencia) {
            setPeriodo([mesCompetencia.startOf("month"), mesCompetencia.endOf("month")]);
            consultarPeriodoFechado();
        }
    }, [mesCompetencia, filterType]);

    // Busca dados ao mudar de mês ou período
    useEffect(() => {
        fetchBalancete();
    }, [mesCompetencia, periodo]);

    // Verifica meses bloqueados ao usar filtro por período
    useEffect(() => {
        if (filterType === "periodo" && periodo && periodo.length === 2) {
            consultarMesesBloqueados(periodo[0], periodo[1]);
        } else {
            setMesesBloqueados([]);
        }
    }, [periodo, filterType]);

    // Resize Observer para ajuste de altura (iframe handling)
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            const height = document.documentElement.scrollHeight;
            window.parent.postMessage({ type: 'resize', height: height }, '*');
        });

        if (document.body) {
            resizeObserver.observe(document.body);
        }

        return () => resizeObserver.disconnect();
    }, [rawData]);

    // --- Lógica de Negócio ---

    const situacaoList = useMemo(() => processarBalanceteContabil(rawData), [rawData]);

    // --- Sincronizar dados com o pai para o Checklist ---
    useEffect(() => {
        if (onDataChange) {
            onDataChange({ situacaoList, loading });
        }
    }, [situacaoList, loading, onDataChange]);

    const fetchBalancete = useCallback(async () => {
        if (filterType === "mensal" && !mesCompetencia) return;
        if (filterType === "periodo" && (!periodo || periodo.length !== 2)) return;

        setLoading(true);
        try {
            const param = filterType === "mensal"
                ? {
                    dataInicio: mesCompetencia.startOf('month').hour(12).toISOString(),
                    dataTermino: mesCompetencia.endOf('month').hour(12).toISOString(),
                }
                : {
                    dataInicio: periodo[0].startOf('month').hour(12).toISOString(),
                    dataTermino: periodo[1].endOf('month').hour(12).toISOString(),
                };

            const responseData = await Method_POST_CONTEXTO("/empresa/contabilidade/contas/saldos/pesquisar", param);
            setRawData(Array.isArray(responseData) ? responseData : []);
        } catch (err) {
            console.error("Erro ao buscar balancete:", err);
            toast.error("Não foi possível carregar o balancete.");
            setRawData([]);
        } finally {
            setLoading(false);
        }
    }, [filterType, mesCompetencia, periodo]);

    const uploadObrigacao = useCallback(async () => {
        if (!periodo || periodo.length !== 2) return;
        setLoading(true);
        try {
            const inicio = periodo[0];
            const fim = periodo[1];
            const mesInicio = inicio.month() + 1;
            const mesFim = fim.month() + 1;

            let endpoint = null;
            if (mesInicio === 1 && mesFim === 6) endpoint = "empresa/contabilidade/create/balancete/semestral";
            else if (mesInicio === 1 && mesFim === 9) endpoint = "empresa/contabilidade/create/balancete/terceiroTri";
            else {
                toast.warn("Período inválido para obrigação (Semestral ou Jan-Set).");
                setLoading(false);
                return;
            }

            await Method_POST_CONTEXTO(endpoint, {
                dataInicio: inicio.startOf('month').hour(12).toISOString(),
                dataTermino: fim.endOf('month').hour(12).toISOString(),
                cliente: { id: clienteId },
            });
            toast.success("Obrigação enviada com sucesso!");
        } catch (err) {
            console.error(err);
            toast.error("Erro ao enviar obrigação.");
        } finally {
            setLoading(false);
        }
    }, [periodo, clienteId]);

    const consultarPeriodoFechado = useCallback(async () => {
        if (!mesCompetencia) return;

        try {
            const ano = mesCompetencia.year();
            const mes = mesCompetencia.month() + 1;

            const res = await Method_GET_CONTEXTO(
                `/empresa/contabilidade/consultar/periodo?ano=${ano}&mes=${mes}`
            );

            const fechado = res?.fechado ?? res === true;
            setPeriodoFechado(!!fechado);

        } catch (err) {
            console.error("Erro ao consultar período:", err);
            setPeriodoFechado(false);
        }
    }, [mesCompetencia]);

    const consultarMesesBloqueados = useCallback(async (dataInicio, dataFim) => {
        if (!dataInicio || !dataFim) return;

        let cursor = dataInicio.clone().startOf('month');
        const bloqueados = [];

        while (cursor.isSameOrBefore(dataFim, 'month')) {
            try {
                const res = await Method_GET_CONTEXTO(
                    `/empresa/contabilidade/consultar/periodo?ano=${cursor.year()}&mes=${cursor.month() + 1}`
                );
                if (res) {
                    bloqueados.push(cursor.format('MM/YYYY'));
                }
            } catch (err) {
                console.error('Erro ao consultar mês:', cursor.format('MM/YYYY'), err);
            }
            cursor = cursor.add(1, 'month');
        }

        setMesesBloqueados(bloqueados);
    }, []);

    const handleFaturamento = useCallback(async () => {
        setLoading(true);
        // Formata para o primeiro dia do mês selecionado no formato YYYY-MM-DD
        const dataReferencia = periodo[1].startOf('month').format('YYYY-MM-DD');

        try {
            // MOCK DE DADOS PARA DEMONSTRAR A NOVA TABELA
            toast.info("Exibindo layout com dados mockados de Faturamento.");
            const mockData = Array.from({ length: 12 }).map((_, i) => {
                const month = periodo[1].clone().subtract(11 - i, 'month');
                const receita = Math.random() * 50000 + 100000;
                return {
                    competencia: month.format('MM/YYYY'),
                    receitaBruta: receita,
                    aliquota: 6.5,
                    valorDevido: receita * 0.065
                };
            });
            
            // Simula um pequeno tempo de carregamento
            await new Promise(resolve => setTimeout(resolve, 600));
            
            setFaturamentoData(mockData);
            toggleModal('faturamento', true);

            /* 
            // TODO: Quando a API estiver pronta, descomente e ajuste o código abaixo:
            const url = `/empresa/contabilidade/faturamento/ultimos-12-meses?dataRef=${dataReferencia}`;
            const response = await Method_GET_CONTEXTO(url);
            if (response && Array.isArray(response)) {
                setFaturamentoData(response);
                toggleModal('faturamento', true);
            }
            */

        } catch (err) {
            console.error("Erro faturamento:", err);
            toast.error("Ocorreu um erro ao exibir o faturamento.");
        } finally {
            setLoading(false);
        }
    }, [periodo, toggleModal]);

    const downloadBalancete = useCallback(async () => {
        setLoading(true);
        try {
            await serviceDownloadBalancete(periodo[0].format('YYYY-MM-DD'), periodo[1].format('YYYY-MM-DD'));
        } catch (error) {
            toast.error("Erro ao gerar PDF.");
        } finally {
            setLoading(false);
        }
    }, [periodo]);

    // Atalhos de teclado (Padrão Alt + Shift para segurança total contra atalhos de navegador)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!e.key) return;
            const key = e.key.toLowerCase();

            // Verifica se Alt e Shift estão pressionados simultaneamente
            if (e.altKey && e.shiftKey) {
                switch (key) {
                    case 'n': // Novo Lançamento
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        if (!periodoFechado) {
                            toggleModal('editLancamento', true, { reset: true });
                        } else {
                            toast.warn("Período contábil fechado.");
                        }
                        break;
                    case 'd': // DRE
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        toggleModal('dre', true);
                        break;
                    case 'r': // Livro Razão
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        toggleModal('livroRazao', true, { context: 'menu' });
                        break;
                    case 'b': // Lançamentos Bancários
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        if (!periodoFechado) {
                            toggleModal('lancamentosBancarios', true);
                        } else {
                            toast.warn("Período contábil fechado.");
                        }
                        break;
                    case 'c': // Plano de Contas
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        toggleModal('contas', true);
                        break;
                    case 'e': // Exportar Excel
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        toggleModal('exportExcel', true);
                        break;
                    case 'p': // Gerar PDF
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        downloadBalancete();
                        break;
                    case 'f': // Focar Filtro
                        e.preventDefault();
                        filterRef.current?.focus();
                        break;
                    case 'a': // Patrimonio (Ativo)
                        e.preventDefault();
                        if (!isContador) {
                            toast.error("Acesso restrito a contadores/administradores.");
                            break;
                        }
                        toggleModal('patrimonio', true);
                        break;
                    case 'h': // Ajuda / Toggle Legenda
                        e.preventDefault();
                        setShowShortcuts(prev => !prev);
                        break;
                    default:
                        break;
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [periodoFechado, downloadBalancete]);


    const modalIcons = {
        dre: <PieChartOutlined />,
        livroRazao: <BookOutlined />,
        editLancamento: <SwapOutlined />,
        lancamentosBancarios: <BankOutlined />,
        balanco: <AuditOutlined />,
        pagamentos: <DollarOutlined />,
        contas: <ClusterOutlined />,
        informe: <SolutionOutlined />,
        excel: <FileExcelOutlined />,
        delete: <DeleteOutlined />,
        regerar: <HistoryOutlined />,
        bloqueio: <LockOutlined />,
        deletarBloqueio: <UnlockOutlined />,
        zeramento: <RestOutlined />,
        exportExcel: <CloudDownloadOutlined />,
        faturamento: <DollarOutlined />,
        folha: <SolutionOutlined />,
        patrimonio: <AppstoreOutlined />,
        dashboard: <LayoutOutlined />,

    };

    // --- Arrastar Elementos (Taskbar ou Atalhos) ---
    const handleMouseDown = (e, target) => {
        draggingTarget.current = target;
        const currentPos = target === 'taskbar' ? taskbarPos : shortcutPos;
        offset.current = {
            x: e.clientX - currentPos.x,
            y: e.clientY - currentPos.y
        };
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!draggingTarget.current) return;
        const newPos = {
            x: e.clientX - offset.current.x,
            y: e.clientY - offset.current.y
        };
        if (draggingTarget.current === 'taskbar') {
            setTaskbarPos(newPos);
        } else {
            setShortcutPos(newPos);
        }
    };

    const handleMouseUp = () => {
        draggingTarget.current = null;
        document.body.style.userSelect = 'auto';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };

    return (
        <div className="balancete-page-container" >
            <BalanceteBanner />

            {/* Mensagens de Aviso de Período Bloqueado */}
            {filterType === "mensal" && periodoFechado && (
                <div className="alert-box alert-error">
                    <LockOutlined className="alert-icon" />
                    <div>
                        <div className="alert-title">Período contábil fechado</div>
                        <div className="alert-desc">Este mês está bloqueado e não permite alterações contábeis.</div>
                    </div>
                </div>
            )
            }

            {
                filterType !== "mensal" && mesesBloqueados.length > 0 && (
                    <div className="alert-box alert-error">
                        <LockOutlined className="alert-icon" />
                        <div>
                            <div className="alert-title">Meses bloqueados no período</div>
                            <div className="alert-desc">
                                Os seguintes meses estão bloqueados e não permitem alterações:{" "}
                                <b>{mesesBloqueados[0]} até {mesesBloqueados[mesesBloqueados.length - 1]}</b>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Header e Filtros */}
            <BalanceteHeader
                filterType={filterType}
                setFilterType={setFilterType}
                mesCompetencia={mesCompetencia}
                setMesCompetencia={setMesCompetencia}
                periodo={periodo}
                setPeriodo={setPeriodo}
                loading={loading}
                downloadBalancete={downloadBalancete}
                toggleModal={toggleModal}
                periodoFechado={periodoFechado}
                uploadObrigacao={uploadObrigacao}
                handleFaturamento={handleFaturamento}
                filterRef={filterRef}
                isContador={isContador}
            />

            <Divider style={{ margin: "20px 0" }} />

            {/* Tabela */}
            <BalanceteTable
                loading={loading}
                situacaoList={situacaoList}
                toggleModal={toggleModal}
            />

            {/* Modais */}
            <BalanceteModals
                modals={modals}
                minimized={minimized}
                toggleModal={toggleModal}
                minimizeModal={minimizeModal}
                filterType={filterType}
                mesCompetencia={mesCompetencia}
                periodo={periodo}
                contaSelecionada={contaSelecionada}
                razaoAbertoPor={razaoAbertoPor}
                editingLancamentoId={editingLancamentoId}
                periodoFechado={periodoFechado}
                fetchBalancete={fetchBalancete}
                consultarPeriodoFechado={consultarPeriodoFechado}
                consultarMesesBloqueados={consultarMesesBloqueados}
                defisPendencia={defisPendencia}
                situacaoList={situacaoList}
                faturamentoData={faturamentoData}
                zIndices={zIndices}
                focusModal={focusModal}
            />

            {/* Barra de Modais Minimizados (Premium Taskbar Draggable & Resizable) */}
            {minimized.length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        top: taskbarPos.y,
                        left: taskbarPos.x,
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: isTaskbarCompact ? 8 : 12,
                        zIndex: 9999,
                        background: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(12px)',
                        padding: isTaskbarCompact ? '6px 12px' : '8px 16px',
                        borderRadius: isTaskbarCompact ? '12px' : '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        maxWidth: '95%',
                        overflowX: 'auto',
                        pointerEvents: 'auto',
                        transition: draggingTarget.current === 'taskbar' ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {/* Alça de Arraste */}
                    <div
                        onMouseDown={(e) => handleMouseDown(e, 'taskbar')}
                        style={{
                            cursor: 'move',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px',
                            color: '#94a3b8',
                            marginRight: 4,
                            borderRadius: '4px',
                        }}
                    >
                        <HolderOutlined style={{ fontSize: isTaskbarCompact ? '14px' : '18px' }} />
                    </div>

                    {/* Botão de Expandir/Recolher */}
                    <Button
                        type="text"
                        size="small"
                        icon={isTaskbarCompact ? <FullscreenOutlined /> : <FullscreenExitOutlined />}
                        onClick={() => setIsTaskbarCompact(!isTaskbarCompact)}
                        style={{
                            color: '#64748b',
                            fontSize: '14px',
                            marginRight: 4
                        }}
                    />

                    {!isTaskbarCompact ? (
                        <>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginRight: 8,
                                color: '#475569',
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.8px',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                userSelect: 'none'
                            }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', marginRight: 8, animation: 'pulse 2s infinite' }}></div>
                                Janelas
                            </div>

                            {minimized.map(key => (
                                <div key={key} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <Button
                                        icon={modalIcons[key] || <FileTextOutlined />}
                                        onClick={() => toggleModal(key, true)}
                                        className="minimized-modal-btn"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            height: '34px',
                                            padding: '0 10px',
                                            borderRadius: '10px',
                                            border: '1px solid #e2e8f0',
                                            background: '#fff',
                                            color: '#1e293b',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                                            paddingRight: '30px'
                                        }}
                                    >
                                        {modalNames[key] || key}
                                    </Button>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<div style={{ fontSize: '9px', fontWeight: 'bold' }}>✕</div>}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMinimized(prev => prev.filter(m => m !== key));
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: 5,
                                            width: 18,
                                            height: 18,
                                            minWidth: 18,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#94a3b8',
                                            borderRadius: '4px',
                                        }}
                                        className="close-mini-btn"
                                    />
                                </div>
                            ))}
                        </>
                    ) : (
                        <Dropdown
                            placement="bottom"
                            trigger={['click']}
                            overlay={
                                <div style={{
                                    background: '#fff',
                                    padding: '8px',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    minWidth: '200px',
                                    marginTop: '8px'
                                }}>
                                    <div style={{ padding: '8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', borderBottom: '1px solid #f1f5f9', marginBottom: '8px' }}>
                                        Selecionar Janela
                                    </div>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {minimized.map(key => (
                                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '4px' }}>
                                                <Button
                                                    type="text"
                                                    icon={modalIcons[key]}
                                                    onClick={() => toggleModal(key, true)}
                                                    style={{ flex: 1, textAlign: 'left', fontWeight: 600 }}
                                                >
                                                    {modalNames[key] || key}
                                                </Button>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<div>✕</div>}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMinimized(prev => prev.filter(m => m !== key));
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </Space>
                                </div>
                            }
                        >
                            <Badge count={minimized.length} offset={[2, 0]} size="small" style={{ backgroundColor: '#22c55e' }}>
                                <Button
                                    icon={<WindowsOutlined />}
                                    type="primary"
                                    style={{
                                        borderRadius: '8px',
                                        height: '32px',
                                        width: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                />
                            </Badge>
                        </Dropdown>
                    )}

                    <style>{`
                        @keyframes pulse {
                            0% { opacity: 0.5; transform: scale(1); }
                            50% { opacity: 1; transform: scale(1.2); }
                            100% { opacity: 0.5; transform: scale(1); }
                        }
                        .minimized-modal-btn:hover {
                            background: #f8fafc !important;
                            border-color: #cbd5e1 !important;
                            transform: translateY(-1px);
                            box-shadow: 0 4px 8px rgba(0,0,0,0.08) !important;
                        }
                        .close-mini-btn:hover {
                            background: #fee2e2 !important;
                            color: #ef4444 !important;
                        }
                    `}</style>
                </div>
            )}

            {/* Legenda de Atalhos (A Cola) */}
            {showShortcuts && (
                <div style={{
                    position: 'fixed',
                    left: shortcutPos.x,
                    top: shortcutPos.y,
                    zIndex: 999,
                    width: '210px',
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(12px)',
                    padding: '16px',
                    borderRadius: '16px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    transition: draggingTarget.current === 'shortcut' ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                    <div
                        onMouseDown={(e) => handleMouseDown(e, 'shortcut')}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 12,
                            cursor: 'move'
                        }}
                    >
                        <Title level={5} style={{ margin: 0, fontSize: 13, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', pointerEvents: 'none' }}>
                            <QuestionCircleOutlined style={{ marginRight: 6 }} /> Atalhos
                        </Title>
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined style={{ fontSize: 10 }} />}
                            onClick={() => setShowShortcuts(false)}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[
                            { key: 'N', label: 'Novo Lançamento', icon: <PlusOutlined /> },
                            { key: 'B', label: 'Bancários', icon: <BankOutlined /> },
                            { key: 'P', label: 'Gerar PDF', icon: <DownloadOutlined /> },
                            { key: 'F', label: 'Focar Filtro', icon: <SearchOutlined /> },
                            { key: 'D', label: 'DRE', icon: <FileTextOutlined /> },
                            { key: 'R', label: 'Livro Razão', icon: <BookOutlined /> },
                            { key: 'C', label: 'Plano de Contas', icon: <ClusterOutlined /> },
                            { key: 'E', label: 'Exportar Excel', icon: <FileExcelOutlined /> },
                            { key: 'A', label: 'Patrimônio', icon: <AppstoreOutlined /> },
                            { key: 'H', label: 'Ocultar Legenda', icon: <MinusOutlined /> },
                        ].map(item => (
                            <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                    <span style={{ fontSize: 12, color: '#64748b' }}>{item.icon}</span>
                                    <Text style={{ fontSize: 11, color: '#334155', whiteSpace: 'nowrap' }}>{item.label}</Text>
                                </div>
                                <Text strong style={{
                                    fontSize: 10,
                                    backgroundColor: '#eef2ff',
                                    color: '#4f46e5',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    border: '1px solid #e0e7ff',
                                    fontFamily: 'monospace'
                                }}>
                                    {item.key}
                                </Text>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                        <Text style={{ fontSize: 9, color: '#94a3b8', display: 'block', textAlign: 'center' }}>
                            Padrão: <Text strong style={{ fontSize: 9 }}>Alt + Shift + Tecla</Text>
                        </Text>
                    </div>
                </div>
            )}

            {/* Botão de Ajuda Flutuante (quando a legenda está oculta) */}
            {!showShortcuts && (
                <Tooltip title="Atalhos (Alt + Shift + H)">
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setShowShortcuts(true)}
                        style={{
                            position: 'fixed',
                            right: 24,
                            bottom: 24,
                            zIndex: 999,
                            width: 48,
                            height: 48,
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
                        }}
                    />
                </Tooltip>
            )}
        </div>
    );
}
